import classNames from 'classnames';
import { Fragment, useEffect, useRef } from 'react';
import { useTimeIndicator } from 'features/meetings/hooks';
import { type Meeting, type MeetingGroupEvent, isEmptyMeetingEvent } from 'features/meetings/types';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { DateBadge } from './DateBadge';
import { EmptyState } from './EmptyState';
import { MeetingCard } from '../../meeting_card';
const meetingGroupCss = "mb9wxna";

/** Preset header shows the active date; drop extra leading margin under the filter fade. */
const meetingGroupPresetHeaderCss = "m7d9gqa";
const meetingsListCss = "m1vxnfka";
interface MeetingGroupProps {
  date: string;
  rawDate?: string;
  events: MeetingGroupEvent[];
  selectedMeetingId?: string;
  showBuffers?: boolean;
  anyInProgress?: boolean;
  packageNameMap?: Record<string, string>;
  onboardingTourMeetingRefCallback?: (el: HTMLElement | null) => void;
  onboardingTourNotetakerRefCallback?: (el: HTMLElement | null) => void;
  firstUpcomingEventKey?: string;
  registerDateGroupRef?: (date: string, el: HTMLElement | null) => void;
  linkPathname?: string;
  /** When preset tabs show the date, omit the per-group DateBadge and tighten top spacing. */
  usePresetListHeader?: boolean;
}

/**
 * Renders a group of meetings for a single date, including the date badge,
 * meeting cards, and current time indicator. On desktop, the date badge
 * is displayed alongside the meetings list; on mobile, it stacks above
 * with sticky positioning.
 *
 * @param date - Formatted display date string shown in the DateBadge
 * @param rawDate - Optional raw ISO date string used for scroll anchoring and time indicator comparisons
 * @param events - Ordered list of meeting events (or empty state placeholders) for this date
 * @param selectedMeetingId - UUID of the currently selected meeting, used to highlight the card
 * @param showBuffers - Whether to render buffer time indicators on meeting cards
 * @param anyInProgress - Whether any meeting in the list is currently in progress
 * @param packageNameMap - Map of meeting UUID to package name for package booking display
 * @param onboardingTourMeetingRefCallback - Ref callback for attaching the onboarding tour anchor to a meeting card
 * @param onboardingTourNotetakerRefCallback - Ref callback for attaching the onboarding tour anchor to a notetaker control
 * @param firstUpcomingEventKey - Key of the first upcoming event, used to identify the tour target card
 * @param registerDateGroupRef - Callback to register this group's DOM element by date for scroll-to-date navigation
 */
export const MeetingGroup = ({
  date,
  rawDate,
  events,
  selectedMeetingId,
  showBuffers,
  anyInProgress,
  packageNameMap = {},
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  firstUpcomingEventKey,
  registerDateGroupRef,
  linkPathname,
  usePresetListHeader = false
}: MeetingGroupProps) => {
  const groupRef = useRef<HTMLDivElement>(null);
  const firstEvent = events[0];
  const firstEventDate = firstEvent && !isEmptyMeetingEvent(firstEvent) ? firstEvent.date : undefined;
  const comparableDate = rawDate || firstEventDate || date;
  const dateForComparison = rawDate || firstEventDate || null;
  const {
    indicatorIndex,
    mostRecentInProgressIndex,
    showIndicatorAfterAll,
    hasRealtimeFeatures
  } = useTimeIndicator(comparableDate, events);
  useEffect(() => {
    if (registerDateGroupRef && dateForComparison) {
      registerDateGroupRef(dateForComparison, groupRef.current);
      return () => {
        registerDateGroupRef(dateForComparison, null);
      };
    }
    return undefined;
  }, [registerDateGroupRef, dateForComparison, groupRef]);
  return <div ref={groupRef} data-date={dateForComparison} className={classNames(meetingGroupCss, {
    [meetingGroupPresetHeaderCss]: usePresetListHeader
  })}>
      {!usePresetListHeader && <DateBadge date={rawDate || date} />}
      <div className={meetingsListCss}>
        {events.map((event, index, list) => {
        const {
          key
        } = event;
        const isLast = index === list.length - 1;
        const isFirst = index === 0;
        const showIndicatorBefore = index === indicatorIndex;
        const isMostRecentInProgress = index === mostRecentInProgressIndex;
        return <Fragment key={key}>
              {showIndicatorBefore && <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />}
              {isEmptyMeetingEvent(event) ? <EmptyState type={event.type} /> : <MeetingCard anyInProgress={anyInProgress} event={event} isFirst={isFirst} isLast={isLast} isMostRecentInProgress={isMostRecentInProgress} isNext={showIndicatorBefore} otherEventsOnDay={list.filter((e): e is Meeting => !isEmptyMeetingEvent(e) && e.uuid !== event.uuid)} selectedEventId={selectedMeetingId} showBuffers={showBuffers} packageName={packageNameMap[event.uuid]} onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback} onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback} isFirstUpcomingForTour={(event.key ?? event.uuid) === firstUpcomingEventKey} linkPathname={linkPathname} />}
            </Fragment>;
      })}
        {showIndicatorAfterAll && <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />}
      </div>
    </div>;
};

require("./MeetingGroup.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MeetingGroup.tsx");